import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { 
  ChartBarIcon, 
  TrophyIcon, 
  ClockIcon,
  UserGroupIcon,
  MapPinIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
// import { FallbackImage } from './FallbackImage';

interface Benefit {
  title: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  image: string;
  imageAlt: string;
}

const BENEFITS: Benefit[] = [
  {
    title: "Stop Guessing, Start Improving",
    description: "No more wondering why your scores fluctuate. Get detailed analytics that show exactly where you're excelling and where you need focus. Track your weekly averages, identify patterns, and see measurable improvement over time with interactive charts and trend analysis.",
    icon: ChartBarIcon,
    image: "/screenshots/performance-chart.svg",
    imageAlt: "Performance analytics chart showing bowling score trends over time"
  },
  {
    title: "Know Where You Stand",
    description: "Are you above or below league average? How do you compare to other players? Get instant context for your performance with comprehensive league comparisons. See your ranking, understand your strengths, and identify opportunities to climb the leaderboard.",
    icon: TrophyIcon,
    image: "/screenshots/comparison-view.svg",
    imageAlt: "Player comparison dashboard showing rankings and statistics"
  },
  {
    title: "Optimize Your Lane Strategy",
    description: "Some lanes just feel different, and now you'll know why. Discover which lanes you perform best on and adjust your strategy accordingly. Our lane analysis reveals patterns you never knew existed, helping you make smarter decisions on game night.",
    icon: MapPinIcon,
    image: "/screenshots/lane-analysis.svg",
    imageAlt: "Lane analysis showing performance breakdown by bowling lane"
  },
  {
    title: "Save Hours of Manual Tracking",
    description: "Stop keeping scorecards and calculating averages by hand. Your statistics are automatically updated and organized, giving you more time to focus on your game. Access your complete bowling history instantly, from any device, whenever you need it.",
    icon: ClockIcon,
    image: "/screenshots/dashboard-desktop.svg",
    imageAlt: "Dashboard showing automated statistics tracking and organization"
  },
  {
    title: "Connect with Your League Community",
    description: "Bowling is more fun with friends. See how your teammates are performing, celebrate improvements together, and create friendly competition. Share achievements and support each other's progress with integrated social features.",
    icon: UserGroupIcon,
    image: "/screenshots/mobile-interface.svg",
    imageAlt: "Mobile interface showing league community features"
  },
  {
    title: "Turn Data into Wins",
    description: "Raw numbers don't win games - insights do. Our platform transforms your bowling data into actionable recommendations. Identify your most consistent conditions, understand your improvement trajectory, and make data-driven decisions that lead to higher scores.",
    icon: ArrowTrendingUpIcon,
    image: "/screenshots/game-history.svg",
    imageAlt: "Game history analysis showing data-driven insights and recommendations"
  }
];

interface BenefitsSectionProps {
  benefits?: Benefit[];
}

export function BenefitsSection({ benefits = BENEFITS }: BenefitsSectionProps) {
  return (
    <section 
      className="py-20 bg-white"
      aria-labelledby="benefits-heading"
      role="region"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.h2 
            id="benefits-heading"
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Bowlers Love Our Platform
          </motion.h2>
          <motion.p 
            className="text-xl text-gray-600 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
            aria-describedby="benefits-heading"
          >
            From casual players to serious competitors, discover how our analytics platform 
            helps bowlers at every level improve their game and enjoy the sport more.
          </motion.p>
        </div>

        {/* Benefits List */}
        <div className="space-y-20" role="list" aria-label="Platform benefits">
          {benefits.map((benefit, index) => (
            <BenefitItem 
              key={benefit.title}
              benefit={benefit}
              isReversed={index % 2 === 1}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface BenefitItemProps {
  benefit: Benefit;
  isReversed: boolean;
  index: number;
}

function BenefitItem({ benefit, isReversed, index }: BenefitItemProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  const imageVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94] as const
      }
    }
  };

  return (
    <motion.article
      ref={ref}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      className={`flex flex-col ${isReversed ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 lg:gap-16 max-w-none overflow-hidden`}
      role="listitem"
      aria-labelledby={`benefit-title-${index}`}
      aria-describedby={`benefit-desc-${index}`}
    >
      {/* Content */}
      <motion.div 
        variants={itemVariants}
        className="flex-1 text-center lg:text-left"
      >
        {/* Icon */}
        <motion.div 
          variants={itemVariants}
          className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6"
          aria-hidden="true"
        >
          <benefit.icon className="w-8 h-8 text-blue-600" />
        </motion.div>

        {/* Title */}
        <motion.h3 
          id={`benefit-title-${index}`}
          variants={itemVariants}
          className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4"
        >
          {benefit.title}
        </motion.h3>

        {/* Description */}
        <motion.p 
          id={`benefit-desc-${index}`}
          variants={itemVariants}
          className="text-lg text-gray-600 leading-relaxed"
        >
          {benefit.description}
        </motion.p>
      </motion.div>

      {/* Image */}
      <motion.div 
        variants={imageVariants}
        className="flex-1 max-w-lg w-full"
      >
        <div className="relative">
          {/* Background decoration */}
          <div className="absolute -inset-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl transform rotate-1"></div>
          
          {/* Image container */}
          <div className="relative bg-white rounded-xl shadow-xl overflow-hidden">
            <img
              src={benefit.image}
              alt={benefit.imageAlt}
              className="w-full h-auto"
              loading="lazy"
              onError={(error) => {
                console.warn('Benefit image failed to load:', benefit.title, error);
                // Set a placeholder background on error
                const target = error.target as HTMLImageElement;
                target.style.backgroundColor = '#f3f4f6';
                target.style.minHeight = '200px';
                target.style.display = 'flex';
                target.style.alignItems = 'center';
                target.style.justifyContent = 'center';
              }}
              onLoad={() => {
                console.log('Benefit image loaded successfully:', benefit.title);
              }}
              role="img"
              aria-describedby={`benefit-desc-${index}`}
            />
          </div>
        </div>
      </motion.div>
    </motion.article>
  );
}

export default BenefitsSection;